// src/components/ModelCard/ModelCard.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import TagManager from 'react-gtm-module';
import LazyImage from '../LazyImage';
import ModelModal from './ModelModal';
import ModelPreview from './ModelPreview';
import styles from './ModelCard.module.scss';

const MotionLink = motion(Link);
const MotionButton = motion.button;

const imageHover = { scale: 1.1 };
const imageTransition = { type: 'tween', ease: 'easeOut', duration: 0.3 };

const titleHover = {
  scale: 1.05,
  y: -2,
  textShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
};
const titleTransition = { type: 'tween', ease: 'easeOut', duration: 0.2 };

const verMasHover = {
  backgroundColor: '#BCBDC0',
  color: 'black',
  scale: 1.05,
  y: -3,
};
const verMasTransition = { type: 'tween', ease: 'easeOut', duration: 0.2 };

const detallesHover = {
  backgroundColor: '#E0E0E0',
  color: '#000',
  scale: 1.05,
  y: -2,
};
const detallesTransition = { type: 'tween', ease: 'easeOut', duration: 0.2 };

function ModelCard({ slug, nombre, imagen, especificaciones, selectedLineaSlug }) {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isPreviewOpen, setPreviewOpen] = useState(false);
  const [canClick, setCanClick] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia('(pointer: fine)');
    setCanClick(mql.matches);
    const handler = e => setCanClick(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const toggleModal = () => {
    if (typeof window !== 'undefined') {
      TagManager.dataLayer({
        dataLayer: {
          event: 'ux_event',
          event_name: 'productos_model_view_details',
          event_category: 'engagement',
          model_name: nombre,
          value: 1
        }
      });
    }
    setModalOpen(open => !open);
  };

  const handleVerMasClick = () => {
    if (typeof window !== 'undefined') {
      TagManager.dataLayer({
        dataLayer: {
          event: 'ux_event',
          event_name: 'productos_model_view_more',
          event_category: 'navigation',
          model_name: nombre,
          value: 1
        }
      });
    }
  };

  const handleImageClick = () => {
    if (!canClick) return;
    if (typeof window !== 'undefined') {
      TagManager.dataLayer({
        dataLayer: {
          event: 'ux_event',
          event_name: 'productos_model_preview_image',
          event_category: 'engagement',
          model_name: nombre,
          value: 1
        }
      });
    }
    setPreviewOpen(true);
  };

  return (
    <div className={styles.modelCard}>
      <motion.div
        className={canClick ? styles.clickableImage : undefined}
        onClick={canClick ? handleImageClick : undefined}
        style={{ cursor: canClick ? 'pointer' : 'default' }}
        whileHover={imageHover}
        transition={imageTransition}
      >
        <LazyImage
          src={imagen}
          alt={nombre}
          className={styles.modelCardImage}
        />
      </motion.div>

      <motion.h3
        className={styles.modelCardName}
        whileHover={titleHover}
        transition={titleTransition}
      >
        {nombre}
      </motion.h3>

      <ul className={styles.modelCardSpecs}>
        {especificaciones.slice(0, 3).length > 0 ? (
          especificaciones.slice(0, 3).map((spec, i) => (
            <li key={i}>
              <span className={styles.modelCardSpecName}>
                {spec.name}:
              </span>
              {spec.value}
            </li>
          ))
        ) : (
          <li className={styles.noSpecsMessage}>
            No hay especificaciones disponibles
          </li>
        )}
      </ul>

      <div className={styles.modelCardFooter}>
        <MotionLink
          to={`/productos/${selectedLineaSlug}/${slug}`}
          className={`${styles.modelCardButton} ${styles.vermasButton}`}
          onClick={handleVerMasClick}
          whileHover={verMasHover}
          transition={verMasTransition}
        >
          VER MÁS
        </MotionLink>

        <MotionButton
          className={`${styles.modelCardButton} ${styles.modalButton}`}
          onClick={toggleModal}
          whileHover={detallesHover}
          transition={detallesTransition}
        >
          DETALLES
        </MotionButton>
      </div>

      {isModalOpen && (
        <ModelModal
          especificaciones={especificaciones}
          onClose={toggleModal}
          nombre={nombre}
        />
      )}

      {isPreviewOpen && (
        <ModelPreview
          src={imagen}
          nombre={nombre}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}

export default ModelCard;